import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import {
  collectPayment,
  getPaymentStatus,
} from "../../../../services/payment.service";

export default function AgentOrderPayment() {
  const { token } = useSelector((state) => state?.auth);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const orderId = params.get("id");
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [isLoading2, setIsLoading2] = useState(false);

  const fetchPaymentStatus = async () => {
    try {
      const res = await getPaymentStatus(token, orderId);

      if (res) {
        // console.log(res.data);

        setPaymentStatus(res.data);
      } else {
        Swal.fire(
          "Error",
          "Failed to fetch payment status. Please try again.",
          "error"
        );
      }
    } catch (error) {
      console.error("Error fetching payment status:", error);
      Swal.fire(
        "Error",
        "An error occurred while fetching the payment status. Please try again later.",
        "error"
      );
    }
  };

  useEffect(() => {
    if (orderId) {
      fetchPaymentStatus();
    }
  }, [orderId]);

  // console.log(orderId);

  const handleCollectPayment = async () => {
    setIsLoading2(true);

    try {
      const remainingAmount = paymentStatus?.remaining_amount;

      const { value: paymentAmount } = await Swal.fire({
        title: "Enter Amount to Pay",
        input: "number",
        inputAttributes: {
          min: remainingAmount < 0 ? -Infinity : 0.01,
          max: remainingAmount,
          step: 0.01,
        },
        inputValue: null,
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Collect",
      });

      if (paymentAmount) {
        const amount = parseFloat(paymentAmount);

        console.log("Payment Amount Entered:", paymentAmount);
        console.log("Remaining Amount:", remainingAmount);

        if (isNaN(amount)) {
          Swal.fire(
            "Invalid Amount",
            "Please enter a valid payment amount.",
            "error"
          );
          return;
        }

        if (
          (remainingAmount < 0 && amount > remainingAmount) ||
          (remainingAmount > 0 && (amount < 0 || amount > remainingAmount))
        ) {
          Swal.fire(
            "Invalid Payment Amount",
            `The entered amount is invalid for the remaining balance of ₹${remainingAmount}. Please try again.`,
            "error"
          );
          return;
        }

        const params = { payment_amount: amount };

        const res = await collectPayment(token, orderId, params);

        if (res && res.status >= 200 && res.status < 300) {
          Swal.fire({
            title: "Success!",
            text: `Payment of ₹${(amount * 1)?.toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })} collected successfully.`,
            icon: "success",
            confirmButtonText: "OK",
          }).then(() => {
            fetchPaymentStatus();
          });
        } else {
          Swal.fire(
            "Error",
            res?.data?.message ||
              "Failed to collect payment. Please try again.",
            "error"
          );
        }
      }
    } catch (error) {
      console.error("Error collecting payment:", error);
      Swal.fire(
        "Error",
        error?.response?.data?.error ||
          "A network or server error occurred. Please try again later.",
        "error"
      );
    } finally {
      setIsLoading2(false);
    }
  };

  return (
    <div className="order-details-container row">
      {/* Left Side: Order Details */}
      <div className="col-sm-12 col-lg-6 col-xl-6 p-2 mb-2">
        <div className="card">
          <div className="card-header">
            <h4 className="card-title">Order Payment Details</h4>
          </div>
          <div className="card-body py-3">
            <div>
              <div className="mb-4">
                <label className="form-label">Sub Total</label>
                <div>
                  ₹
                  {(paymentStatus?.data?.subtotal * 1)?.toLocaleString(
                    "en-IN",
                    {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }
                  )}
                </div>
              </div>
              <div className="mb-4">
                <label className="form-label">Total Tax</label>
                <div>
                  ₹
                  {(paymentStatus?.data?.tax * 1)?.toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </div>
              </div>

              <div className="mb-4">
                <label className="form-label">Shipping Fee</label>
                <div>
                  ₹
                  {(paymentStatus?.data?.shipping_fees * 1)?.toLocaleString(
                    "en-IN",
                    {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }
                  )}
                </div>
              </div>
              <div className="mb-4">
                <label className="form-label">Total Amount</label>
                <div>
                  ₹
                  {(paymentStatus?.data?.final_amount * 1)?.toLocaleString(
                    "en-IN",
                    {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }
                  )}
                </div>
              </div>
              <div className="mb-4">
                <label className="form-label">Payout Status</label>
                <div>
                  <strong>{paymentStatus?.data?.payout_status}</strong>
                </div>
              </div>

              {paymentStatus &&
                paymentStatus?.remaining_amount !== 0 &&
                (!isLoading2 ? (
                  <div className="">
                    <button
                      type="button"
                      className="btn btn-primary w-100"
                      onClick={handleCollectPayment}
                    >
                      {paymentStatus?.remaining_amount < 0
                        ? "RETURN PAYMENT"
                        : "COLLECT PAYMENT"}
                    </button>
                  </div>
                ) : (
                  <div className="">
                    <button
                      type="button"
                      className="btn btn-primary w-100"
                      disabled
                    >
                      {paymentStatus?.remaining_amount < 0
                        ? "RETURNING PAYMENT"
                        : "COLLECTING PAYMENT"}
                    </button>

                    <div className="spinner-border2 text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right Side: Payment Summary */}
      <div className="col-sm-12 col-lg-6 col-xl-6 p-2 mb-2">
        <div className="card">
          <div className="card-header">
            <h4 className="card-title">Payment Summary</h4>
          </div>
          <div className="card-body py-3">
            <div>
              <div className="mb-4">
                <label className="form-label">Total Amount</label>
                <div>
                  ₹
                  {(paymentStatus?.data?.final_amount * 1)?.toLocaleString(
                    "en-IN",
                    {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }
                  )}
                </div>
              </div>
              <div className="mb-4">
                <label className="form-label">Amount Paid</label>
                <div>
                  ₹
                  {(paymentStatus?.data?.total_paid * 1)?.toLocaleString(
                    "en-IN",
                    {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }
                  )}
                </div>
              </div>
              <div className="mb-4">
                <label className="form-label">Remaining Amount</label>
                <div>
                  ₹
                  {(paymentStatus?.remaining_amount * 1)?.toLocaleString(
                    "en-IN",
                    {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }
                  )}
                </div>
              </div>
              {paymentStatus?.payment_summary?.length > 0 && (
                <div className="mb-4">
                  <label className="form-label">Payment History</label>
                  <ul className="list-group">
                    {paymentStatus?.payment_summary?.map((item, i) => (
                      <li key={i} className="list-group-item">
                        <strong>
                          ₹
                          {(item?.amount * 1)?.toLocaleString("en-IN", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </strong>{" "}
                        -{" "}
                        {new Intl.DateTimeFormat("en-IN", {
                          timeZone: "Asia/Kolkata",
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        })
                          .format(new Date(item?.payment_date))
                          .replace(/am|pm/i, (match) => match.toUpperCase())}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
